import axios from 'axios';

// Configuración base de la API
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Crear instancia de axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/admin';
    }
    return Promise.reject(error);
  }
);

// 🔐 Servicios de autenticación
export const authService = {
  async login(username, password) {
    const response = await api.post('/auth/login', { username, password });
    if (response.data.success) {
      localStorage.setItem('authToken', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  },

  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async changePassword(currentPassword, newPassword) {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  },

  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
};

// 📱 Servicios de dispositivos
export const deviceService = {
  async getDevices() {
    const response = await api.get('/admin/devices');
    return response.data;
  },

  async suspendDevice(deviceId) {
    const response = await api.post(`/admin/devices/${deviceId}/suspend`);
    return response.data;
  },

  async activateDevice(deviceId) {
    const response = await api.post(`/admin/devices/${deviceId}/activate`);
    return response.data;
  },

  async sendCommand(deviceId, commandType, payload = {}) {
    const response = await api.post(`/admin/devices/${deviceId}/command`, {
      command_type: commandType,
      payload
    });
    return response.data;
  },

  async startRemoteSession(deviceId) {
    const response = await api.post('/remote/session/start', { device_id: deviceId });
    return response.data;
  },

  async endRemoteSession(token) {
    const response = await api.post(`/remote/session/end/${token}`);
    return response.data;
  },

  async getRemoteSessionStatus(token) {
    const response = await api.get(`/remote/session/${token}`);
    return response.data;
  },

  async getRemoteHistory(limit = 50, offset = 0) {
    const response = await api.get(`/remote/sessions/history?limit=${limit}&offset=${offset}`);
    return response.data;
  },
};

// 📊 Servicios de estadísticas
export const statsService = {
  async getStats() {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  async getPublicStats() {
    const response = await api.get('/public-stats');
    return response.data;
  }
};

export const adminService = {
  async getMyPermissions() {
    const response = await api.get('/admin/me/permissions');
    return response.data;
  },

  async getProjects() {
    const response = await api.get('/admin/projects');
    return response.data;
  },

  async createProject(projectData) {
    const response = await api.post('/admin/projects', projectData);
    return response.data;
  },

  async updateProject(projectId, patch) {
    const response = await api.put(`/admin/projects/${projectId}`, patch);
    return response.data;
  },

  async getAdmins() {
    const response = await api.get('/admin/admins');
    return response.data;
  },

  async createAdmin(payload) {
    const response = await api.post('/admin/admins', payload);
    return response.data;
  },

  async getProjectAdmins(projectId) {
    const response = await api.get(`/admin/projects/${projectId}/admins`);
    return response.data;
  },

  async assignAdminToProject(projectId, adminUserId) {
    const response = await api.post(`/admin/projects/${projectId}/admins/${adminUserId}`);
    return response.data;
  },

  async removeAdminFromProject(projectId, adminUserId) {
    const response = await api.delete(`/admin/projects/${projectId}/admins/${adminUserId}`);
    return response.data;
  },

  async reassignDeviceProject(deviceId, projectId) {
    const response = await api.put(`/admin/devices/${deviceId}/project`, {
      project_id: projectId,
    });
    return response.data;
  },

  async updateDeviceProfile(deviceId, profilePatch) {
    const response = await api.put(`/admin/devices/${deviceId}/profile`, profilePatch);
    return response.data;
  },

  async getDeviceHistory(deviceId) {
    const response = await api.get(`/admin/devices/${deviceId}/history`);
    return response.data;
  },

  async updateProjectBillingStatus(projectId, billingStatus, reason, nextBillingDate) {
    const response = await api.post(`/admin/projects/${projectId}/billing`, {
      billing_status: billingStatus,
      reason,
      next_billing_date: nextBillingDate,
    });
    return response.data;
  },

  async registerProjectPayment(projectId, paymentData) {
    const response = await api.post(`/admin/projects/${projectId}/billing/payment`, paymentData);
    return response.data;
  },

  async getProjectBillingHistory(projectId) {
    const response = await api.get(`/admin/projects/${projectId}/billing/history`);
    return response.data;
  },

  async getEndUsers(query = '') {
    const response = await api.get('/admin/end-users', {
      params: query ? { query } : undefined,
    });
    return response.data;
  },

  async createEndUser(payload) {
    const response = await api.post('/admin/end-users', payload);
    return response.data;
  },

  async linkDeviceEndUser(deviceId, endUserId) {
    const response = await api.put(`/admin/devices/${deviceId}/end-user`, {
      end_user_id: endUserId || null,
    });
    return response.data;
  },
};

// 🔍 Servicio de prueba
export const testService = {
  async testConnection() {
    const response = await api.get('/test');
    return response.data;
  }
};

export default api;
